import { ErrorHandler, Injectable } from '@angular/core'
import { ValidationError } from '@angular/forms/signals'
import { AlvNumeronTarkistustulos } from '@lemon/core-firebase/model/common'
import { LaskunTyypit, LaskunTyyppi } from '@lemon/core-firebase/model/lasku'
import { MaaErikoiskoodit } from '@lemon/core-firebase/service/maa.service-base'
import { VatNumberValidationService } from '@lemon/core-firebase/service/vat-number-validation.service'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { concat, from, Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from '../_jaettu-angular/service/maa.service'
import { AlvNumeronTarkistajaService } from '../_jaettu-angular/service/vat-number-check.service'

export type VatTaiYtunnuksePakollisuus = 'pakollinen-ytunnus' | 'pakollinen-vat' | 'ei-pakollinen' | 'ei-pakollinen-mutta-tarkista-vat-vai-ytunnus' | 'ei-pakollinen-mutta-tarkista-ulkomaan-vat'

@Injectable()
export class LaskunAsiakkaanValidointiService {
  constructor(
    private _errorHandler: ErrorHandler,
    private _validationService: FormValidationService,
    private _maaService: MaaService,
    private _alvNumeronTarkistajaService: AlvNumeronTarkistajaService,
    private _codeCheckService: CodeCheckService,
    private _vatNumberValidationService: VatNumberValidationService,
    private _translationService: LemonTranslationService,
  ) {}

  annaYtunnuksenPakollisuus(tyyppi: LaskunTyyppi, maakoodi: string): VatTaiYtunnuksePakollisuus {
    if (tyyppi?.avain === LaskunTyypit.RAKENNUSALA.avain) {
      return 'pakollinen-ytunnus'
    } else if (tyyppi?.avain === LaskunTyypit.EU_PALVELU.avain || tyyppi?.avain === LaskunTyypit.EU_TAVARA.avain) {
      return 'pakollinen-vat'
    } else if (tyyppi?.avain === LaskunTyypit.TAVALLINEN.avain) {
      return 'ei-pakollinen-mutta-tarkista-vat-vai-ytunnus'
    } else if (tyyppi?.avain === LaskunTyypit.TAVALLINEN_ULKOM.avain) {
      const onkoEunUlkopuolinenMaa = this._maaService.euMaakoodit.indexOf(maakoodi) < 0 && maakoodi !== MaaErikoiskoodit.POHJOIS_IRLANTI_KOODI_ALPHA_3
      if (onkoEunUlkopuolinenMaa) {
        return 'ei-pakollinen'
      }
      return 'ei-pakollinen-mutta-tarkista-ulkomaan-vat'
    }
    return 'ei-pakollinen'
  }

  annaYtunnuksenVirhe(ytunnus: string, maakoodi: string | null, pakollisuus: VatTaiYtunnuksePakollisuus): ValidationError | null {
    switch (pakollisuus) {
      case 'pakollinen-ytunnus':
        return this._tarkistaYtunnus(ytunnus)
      case 'pakollinen-vat':
        return this._tarkistaVatNumero(ytunnus, maakoodi)
      case 'ei-pakollinen-mutta-tarkista-vat-vai-ytunnus':
        return this._tarkistaVatNumeroTaiYtunnus(ytunnus)
      case 'ei-pakollinen-mutta-tarkista-ulkomaan-vat':
        return this._tarkistaUlkomaanVatNumero(ytunnus, maakoodi)
    }
    return null
  }

  annaViesistaTarkistettavaTunnus(ytunnus: string, pakollinen: VatTaiYtunnuksePakollisuus): string | null {
    if (!ytunnus) {
      return null
    }
    if (pakollinen === 'pakollinen-ytunnus') {
      if (this._codeCheckService.isValidYTunnus(ytunnus) || this._vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, 'FI')) {
        return this._codeCheckService.annaVatTunnus(ytunnus)
      } else if (this._vatNumberValidationService.isValidEuVatNumber(ytunnus)) {
        return ytunnus
      }
    } else if ((pakollinen === 'pakollinen-vat' || pakollinen === 'ei-pakollinen-mutta-tarkista-ulkomaan-vat') && this._vatNumberValidationService.isValidEuVatNumber(ytunnus)) {
      return ytunnus
    }
    return null
  }

  tarkistaAlvNumeroViesista(vatTunnus: string): Observable<string> {
    const kasitelty = this._validationService.processValue(vatTunnus)
    const maakoodi = kasitelty.substr(0, 2)
    const numero = kasitelty.substr(2)
    const tulos = this._alvNumeronTarkistajaService
      .tarkistaVatNumero(maakoodi, numero)
      .then(vastaus => {
        if (vastaus.tulos === AlvNumeronTarkistustulos.ONNISTUI) {
          return this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.oikein', { nimi: vastaus.yrityksenNimi, osoite: vastaus.yrityksenOsoite })
        } else if (vastaus.tulos === AlvNumeronTarkistustulos.EU_PALVELIMEN_VIRHE) {
          return this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.epaonnistui-eu')
        } else if (vastaus.tulos === AlvNumeronTarkistustulos.MAAKOODI_VIRHEELLINEN) {
          return this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.virheellinen-maakoodi')
        }
        return this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.virheellinen-numero')
      })
      .catch(error => {
        this._errorHandler.handleError(error)
        return this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.tuntematon-virhe')
      })
    return concat(of(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.tarkistetaan')), from(tulos))
  }

  annaYtunnuksenOtsikkoObservable(tyyppiObservable: Observable<LaskunTyyppi>): Observable<string> {
    return tyyppiObservable.pipe(
      switchMap(tyyppi => {
        return this._translationService.currentLanguageObservable.pipe(
          map(kieli => {
            if (tyyppi.avain === LaskunTyypit.TAVALLINEN.avain || tyyppi.avain === LaskunTyypit.RAKENNUSALA.avain) {
              return this._translationService.lokalisoiKielella('lasku.ytunnus', kieli)
            }
            return this._translationService.lokalisoiKielella('lasku.vat-tunnus', kieli)
          }),
        )
      }),
    )
  }

  suodataMaat(maat: Maa[], tyyppi: LaskunTyyppi): Maa[] {
    if (this.onkoKotimaanLasku(tyyppi)) {
      return maat.filter(maa => maa.koodi === 'FIN')
    } else if (this._onkoEuLasku(tyyppi)) {
      if (this._onkoPohjoisIrlantiLasku(tyyppi)) {
        return maat.filter(maa => (maa.koodi === MaaErikoiskoodit.POHJOIS_IRLANTI_KOODI_ALPHA_3 || this._maaService.euMaakoodit.indexOf(maa.koodi) > -1) && maa.koodi !== 'FIN')
      }
      return maat.filter(maa => this._maaService.euMaakoodit.indexOf(maa.koodi) > -1 && maa.koodi !== 'FIN')
    } else if (this._onkoEunUlkopuolinenLasku(tyyppi)) {
      return maat.filter(maa => this._maaService.euMaakoodit.indexOf(maa.koodi) < 0 && maa.koodi !== MaaErikoiskoodit.POHJOIS_IRLANTI_KOODI_ALPHA_3)
    } else if (this._onkoKotimaanLaskuUlkomaiselleYritykselle(tyyppi)) {
      return maat.filter(maa => maa.koodi !== 'FIN')
    }
    return maat
  }

  onkoKotimaanLasku(tyyppi: LaskunTyyppi): boolean {
    return tyyppi?.avain === LaskunTyypit.TAVALLINEN.avain || tyyppi?.avain === LaskunTyypit.RAKENNUSALA.avain
  }

  private _onkoKotimaanLaskuUlkomaiselleYritykselle(tyyppi: LaskunTyyppi): boolean {
    return tyyppi.avain === LaskunTyypit.TAVALLINEN_ULKOM.avain
  }

  private _onkoPohjoisIrlantiLasku(tyyppi: LaskunTyyppi): boolean {
    return (
      tyyppi.avain === LaskunTyypit.EU_TAVARA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_EI_REKISTEROITYNYT.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_TAVARA.avain
    )
  }

  private _onkoEuLasku(tyyppi: LaskunTyyppi): boolean {
    return (
      tyyppi.avain === LaskunTyypit.EU_TAVARA.avain ||
      tyyppi.avain === LaskunTyypit.EU_PALVELU.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_EI_REKISTEROITYNYT.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_TAVARA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_PALVELU.avain
    )
  }

  private _onkoEunUlkopuolinenLasku(tyyppi: LaskunTyyppi): boolean {
    return tyyppi.avain === LaskunTyypit.MUU_MAAILMA.avain
  }

  private _tarkistaYtunnus(ytunnus: string): ValidationError | null {
    if (ytunnus) {
      if (!this._codeCheckService.isValidYTunnus(ytunnus) && !this._vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, 'FI')) {
        return { kind: 'invalid_ytunnus' }
      }
    }
    return null
  }

  private _tarkistaUlkomaanVatNumero(ytunnus: string, maakoodi: string | null): ValidationError | null {
    if (!maakoodi || !ytunnus) {
      return null
    }
    const alpha2Code = this._maaService.getAlpha2Code(maakoodi)
    if (alpha2Code !== 'FI' && this._vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, alpha2Code)) {
      return null
    }
    return { kind: 'invalid_vat_number' }
  }

  private _tarkistaVatNumero(ytunnus: string, maakoodi: string | null): ValidationError | null {
    if (!maakoodi || !ytunnus) {
      return null
    }
    const alpha2Code = this._maaService.getAlpha2Code(maakoodi)
    if (this._vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, alpha2Code)) {
      return null
    }
    return { kind: 'invalid_vat_number' }
  }

  private _tarkistaVatNumeroTaiYtunnus(ytunnus: string): ValidationError | null {
    if (!ytunnus) {
      return null
    }

    if (this._codeCheckService.isValidYTunnus(ytunnus) || this._vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, 'FI')) {
      return null
    }

    return { kind: 'invalid_vat_number_or_ytunnus' }
  }
}
